import { useState, useEffect, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import FunProgress from '../ui/FunProgress';
import {
  Youtube, AlertCircle, CheckCircle, Download,
  Film, Music, Layers, Clock, Eye, Loader2,
} from 'lucide-react';

type Format = 'best' | 'video' | 'audio';

const YouTubeDownloader = () => {
  const [ytUrl,        setYtUrl]        = useState('');
  const [ytFormat,     setYtFormat]     = useState<Format>('best');
  const [ytQuality,    setYtQuality]    = useState('best');
  const [downloading,  setDownloading]  = useState(false);
  const [result,       setResult]       = useState<any>(null);
  const [error,        setError]        = useState<string | null>(null);
  const [success,      setSuccess]      = useState<string | null>(null);
  const [progress,     setProgress]     = useState(0);
  const [progStatus,   setProgStatus]   = useState('');
  const [videoInfo,    setVideoInfo]    = useState<any>(null);
  const [fetchingInfo, setFetchingInfo] = useState(false);

  const reset = () => {
    setError(null); setSuccess(null); setResult(null);
    setProgress(0); setProgStatus('');
  };

  useEffect(() => {
    if (!ytUrl) { setVideoInfo(null); return; }
    const t = setTimeout(async () => {
      setFetchingInfo(true);
      try {
        const resp = await fetch('/api/tools/youtube/info', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: ytUrl }),
        });
        const data = await resp.json();
        if (data.success) setVideoInfo(data.info);
        else setVideoInfo(null);
      } catch { setVideoInfo(null); }
      finally { setFetchingInfo(false); }
    }, 800);
    return () => clearTimeout(t);
  }, [ytUrl]);

  const handleDownload = async () => {
    reset();
    if (!ytUrl) return setError('Please enter a YouTube URL');
    setDownloading(true);

    try {
      const resp = await fetch('/api/tools/youtube/stream', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: ytUrl, format: ytFormat, quality: ytQuality }),
      });
      if (!resp.ok) throw new Error('Failed to start download');

      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error('No reader');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const line of decoder.decode(value).split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === 'progress') {
              setProgress(d.percent || 0);
              const speedEta = d.speed && d.eta ? ` · ${d.speed} · ETA ${d.eta}` : '';
              setProgStatus((d.status || 'Downloading…') + speedEta);
            }
            else if (d.type === 'complete') {
              setProgress(100); setProgStatus('Complete!');
              setResult({ filename: d.filename, downloadUrl: d.downloadUrl });
              setSuccess(d.message || 'Download complete!');
            } else if (d.type === 'error') { setError(d.error || 'Download failed'); }
          } catch { /* ignore */ }
        }
      }
    } catch (err: any) { setError(err.message || 'Network error'); }
    finally { setDownloading(false); }
  };

  const formatIcons: Record<Format, ReactNode> = {
    best:  <Layers size={15} />,
    video: <Film   size={15} />,
    audio: <Music  size={15} />,
  };
  const formatLabels: Record<Format, string> = {
    best:  'Video + Audio',
    video: 'Video Only',
    audio: 'Audio Only',
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="tool-hero">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#216974] to-[#216974] flex items-center justify-center shadow-lg shadow-[#216974]/30">
            <Youtube size={16} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-[#216974] bg-[#54c4b6]/15 px-2.5 py-1 rounded-full">YouTube</span>
        </div>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">YouTube Downloader</h1>
        <p className="text-sm text-gray-500 mt-1">
          Download videos, playlists, and audio from YouTube at any quality.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-5">
        <Card>
          <div className="space-y-5">
            {/* URL */}
            <Input
              label="YouTube URL"
              placeholder="https://www.youtube.com/watch?v=..."
              value={ytUrl} onChange={(e) => { setYtUrl(e.target.value); reset(); }}
              leftIcon={<Youtube size={16} className="text-[#216974]" />}
              rightIcon={fetchingInfo ? <Loader2 size={14} className="animate-spin text-gray-400" /> : undefined}
            />

            {/* Video info card */}
            <AnimatePresence>
              {videoInfo && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="flex gap-4 p-4 rounded-2xl border border-gray-200/60 bg-gradient-to-r from-[#216974]/60 to-[#216974]/40"
                >
                  {videoInfo.thumbnail && (
                    <img src={videoInfo.thumbnail} alt={videoInfo.title}
                      className="w-24 h-16 object-cover rounded-xl shadow-md shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2">{videoInfo.title}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {videoInfo.duration && (
                        <span className="flex items-center gap-1 text-[11px] text-gray-500">
                          <Clock size={11} /> {videoInfo.duration}
                        </span>
                      )}
                      {videoInfo.viewCount && (
                        <span className="flex items-center gap-1 text-[11px] text-gray-500">
                          <Eye size={11} /> {Number(videoInfo.viewCount).toLocaleString()} views
                        </span>
                      )}
                      <span className="text-[11px] font-semibold text-[#216974] bg-[#54c4b6]/15 px-2 py-0.5 rounded-full">
                        Best: {videoInfo.bestQuality}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Format selector */}
            <div>
              <p className="section-label">Download Type</p>
              <div className="tab-pill-group">
                {(['best', 'video', 'audio'] as Format[]).map((f) => (
                  <button key={f} onClick={() => setYtFormat(f)}
                    className={`tab-pill flex-1 ${ytFormat === f ? 'active' : ''}`}>
                    {formatIcons[f]} {formatLabels[f]}
                  </button>
                ))}
              </div>
            </div>

            {/* Quality */}
            <AnimatePresence>
              {ytFormat === 'audio' && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                  <p className="section-label">Audio Quality</p>
                  <select value={ytQuality} onChange={(e) => setYtQuality(e.target.value)}
                    className="w-full bg-white/70 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#54c4b6]/20 focus:border-[#54c4b6]">
                    <option value="best">Best Quality (320kbps)</option>
                    <option value="192">High (192kbps)</option>
                    <option value="128">Medium (128kbps)</option>
                    <option value="64">Low (64kbps) — smaller file</option>
                  </select>
                </motion.div>
              )}
              {ytFormat !== 'audio' && videoInfo && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                  <p className="section-label">Video Quality</p>
                  <select value={ytQuality} onChange={(e) => setYtQuality(e.target.value)}
                    className="w-full bg-white/70 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#54c4b6]/20 focus:border-[#54c4b6]">
                    <option value="best">Auto Best ({videoInfo?.bestQuality || '…'})</option>
                    {videoInfo?.qualities?.includes('2160p') && <option value="2160p">4K (2160p)</option>}
                    {videoInfo?.qualities?.includes('1440p') && <option value="1440p">2K (1440p)</option>}
                    {videoInfo?.qualities?.includes('1080p') && <option value="1080p">Full HD (1080p)</option>}
                    {videoInfo?.qualities?.includes('720p')  && <option value="720p">HD (720p)</option>}
                    <option value="480p">SD (480p)</option>
                    <option value="360p">Low (360p)</option>
                  </select>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Progress */}
            <AnimatePresence>
              {downloading && progress > 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="glass-card rounded-2xl p-4">
                <FunProgress percent={progress} tool="youtube" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Alerts */}
            <AnimatePresence>
              {error && (
                <motion.div key="err" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="alert-error">
                  <AlertCircle size={16} className="shrink-0" />
                  <p className="text-sm font-medium">{error}</p>
                </motion.div>
              )}
              {success && !result && (
                <motion.div key="ok" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="alert-success">
                  <CheckCircle size={16} className="shrink-0" />
                  <p className="text-sm font-medium">{success}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* CTA */}
            <div className="space-y-3">
              <Button onClick={handleDownload} size="lg" className="w-full"
                isLoading={downloading} disabled={!ytUrl || downloading}
                leftIcon={<Download size={18} />}
              >
                {downloading ? 'Downloading…' : 'Download from YouTube'}
              </Button>

              <AnimatePresence>
                {result && (
                  <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
                    <a href={`${result.downloadUrl}`} download className="block">
                      <Button size="lg" variant="success" className="w-full" leftIcon={<Download size={18} />}>
                        Save — {result.filename}
                      </Button>
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default YouTubeDownloader;
